// Folds the server's append-only event log into what the dock renders.
// A step is announced several times (running, then succeeded...): the latest
// event per step wins, in the position of its first appearance.
import type { AskApproval, AskEvent } from '../api/assistantApi';

export type TimelineItem =
  | { type: 'message'; key: string; event: AskEvent }
  | { type: 'run'; key: string; executionId: string; steps: AskEvent[] }
  | { type: 'approval'; key: string; approval: AskApproval; event: AskEvent }
  | { type: 'result'; key: string; event: AskEvent };

export function foldEvents(events: AskEvent[]): TimelineItem[] {
  const out: TimelineItem[] = [];
  const runs = new Map<string, Extract<TimelineItem, { type: 'run' }>>();
  const approvals = new Map<string, number>();
  for (const e of events) {
    switch (e.kind) {
      case 'message':
        out.push({ type: 'message', key: `m${e.seq}`, event: e });
        break;
      case 'step': {
        const id = e.executionId ?? '';
        let run = runs.get(id);
        if (!run) {
          run = { type: 'run', key: `r${id}`, executionId: id, steps: [] };
          runs.set(id, run);
          out.push(run);
        }
        const at = run.steps.findIndex((s) => s.stepId === e.stepId);
        if (at >= 0) run.steps[at] = e;
        else run.steps.push(e);
        break;
      }
      case 'approval': {
        if (!e.approval) break;
        const item: TimelineItem = { type: 'approval', key: `a${e.approval.id}`, approval: e.approval, event: e };
        const at = approvals.get(e.approval.id);
        if (at !== undefined) out[at] = item;
        else {
          approvals.set(e.approval.id, out.length);
          out.push(item);
        }
        break;
      }
      case 'result':
        out.push({ type: 'result', key: `x${e.seq}`, event: e });
        break;
    }
  }
  return out;
}

/** One short line for the collapsed dock. */
export function statusLine(items: TimelineItem[], activeSummary?: string): { text: string; tone: 'idle' | 'working' | 'ready' | 'attention' | 'error' } {
  if (activeSummary) return { text: activeSummary, tone: 'working' };
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i];
    if (it.type === 'approval') {
      if (it.approval.status === 'pending') return { text: 'Needs your confirmation', tone: 'attention' };
      if (it.approval.status === 'applied') return { text: 'Published', tone: 'ready' };
      if (it.approval.status === 'failed') return { text: 'Publishing failed', tone: 'error' };
      continue;
    }
    if (it.type === 'result') {
      const s = it.event.status;
      if (s === 'completed') return { text: it.event.data?.kind === 'publish' ? 'Published' : 'Ready', tone: 'ready' };
      if (s === 'needs_input') return { text: 'Needs input', tone: 'attention' };
      if (s === 'partial') return { text: 'Partial', tone: 'attention' };
      if (s === 'cancelled') return { text: 'Cancelled', tone: 'idle' };
      return { text: 'Failed', tone: 'error' };
    }
    if (it.type === 'message' && it.event.role === 'assistant') continue;
  }
  return { text: '', tone: 'idle' };
}
